import {AxiosError, HttpStatusCode} from 'axios';
import {t} from 'i18next';
import {useCallback, useEffect, useRef, useState} from 'react';
import {getLocalDir} from '../../api/FCUploadFileServices';
import {showCustomMessageToast} from '../../api/utils';
import {showToastType} from '../../contexts/toastContext/ToastContext';
import {
  FCAPIErrorResponse,
  FCLocalDirectory,
} from '../../types/upload-new-eviidence/fcUploadFileTypes';
import {isAPIRequestAborted} from '../../utils/fcUploadFileUtils';
import {isEmptyInstance} from '../../utils/utils';

const useFCLocalFiles = (
  isAgentRunning: boolean,
  connectionKey: string,
  showToast: showToastType
) => {
  const [currentDirectory, setCurrentDirectory] = useState<string>('');
  const [directoryFiles, setDirectoryFiles] = useState<FCLocalDirectory[]>([]);
  const [isLocalFilesLoading, setIsLocalFilesLoading] =
    useState<boolean>(false);

  const abortController = useRef<AbortController>();

  const showErrorToast = useCallback(
    (errorMessage: string) => {
      !isEmptyInstance(errorMessage) &&
        showCustomMessageToast(showToast, errorMessage);
    },
    [showToast]
  );

  const fetchFilesForCurrentDirectory = useCallback(
    async (path: string) => {
      setCurrentDirectory(path);
      setIsLocalFilesLoading(true);
      try {
        abortController.current?.abort();
        abortController.current = new AbortController();
        const response = await getLocalDir(
          `/${path}`,
          abortController.current?.signal
        );

        if (response.status === HttpStatusCode.Ok) {
          setDirectoryFiles(
            (response?.data?.fileObjectList ?? []).map(
              (item: FCLocalDirectory) => ({
                ...item,
                id: `${item?.path ?? ''}`,
              })
            )
          );
        }
      } catch (e) {
        console.error(e);
        !isAPIRequestAborted(e as AxiosError) &&
          showErrorToast(
            ((e as AxiosError)?.response?.data as FCAPIErrorResponse)?.reason
              ?.text ?? t('common.somethingWrong')
          );
      } finally {
        setIsLocalFilesLoading(false);
      }
    },
    [showErrorToast]
  );

  useEffect(() => {
    (async () => {
      if (isAgentRunning) {
        try {
          if (connectionKey) {
            const localDrive = await getLocalDir();
            const localDriveRootFiles = localDrive?.data?.fileObjectList ?? [];
            const drivePath = `/${localDriveRootFiles[0]?.path}`;
            const response = await getLocalDir(drivePath);
            setCurrentDirectory(localDriveRootFiles[0]?.path);
            setDirectoryFiles(
              (response?.data?.fileObjectList ?? []).map(
                (item: FCLocalDirectory) => ({
                  ...item,
                  id: `${item?.path ?? ''}`,
                })
              )
            );
          }
        } catch (e) {
          console.error(e);
        }
      }
    })();
  }, [isAgentRunning, connectionKey]);

  return {
    currentDirectory,
    fetchFilesForCurrentDirectory,
    setCurrentDirectory,
    directoryFiles,
    isLocalFilesLoading,
  };
};

export default useFCLocalFiles;
